/**
 * Loading each kind of connected media on the host and turning it into the
 * `genie:input` payload the sandbox expects (see contract).
 *
 * Video and 3D travel as transferable buffers. A transferred buffer is gone from
 * the sender, and the iframe needs the input again every time it reloads, so the
 * original stays here and each send transfers a fresh copy.
 */

import type { ShaderInputType, ShaderModelData, ShaderVideoPayload } from './contract'
import type { HostInputData } from '../types'
import { parseGlb } from './glb'
import { abortError, fetchMediaBlob, throwIfAborted, urlExtension } from './fetch-media'

export const MAX_VIDEO_BYTES = 256 * 1024 * 1024
export const MAX_MODEL_BYTES = 128 * 1024 * 1024

export interface MediaSize {
  width: number
  height: number
}

/** A `genie:input` message body and the buffers to transfer with it. */
export interface ShaderInputMessage {
  data: HostInputData
  transfer: Transferable[]
}

// ---------------------------------------------------------------------------
// Image
// ---------------------------------------------------------------------------

export interface LoadedImage extends MediaSize {
  dataUrl: string
}

export async function loadImageSource(url: string, signal?: AbortSignal): Promise<LoadedImage> {
  const dataUrl = url.startsWith('data:') ? url : await blobToDataUrl(await fetchMediaBlob(url, { signal }))
  throwIfAborted(signal)
  const size = await imageSize(dataUrl, signal)
  return { dataUrl, ...size }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('The image could not be read.'))
    reader.readAsDataURL(blob)
  })
}

function imageSize(src: string, signal?: AbortSignal): Promise<MediaSize> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const onAbort = () => reject(abortError())
    signal?.addEventListener('abort', onAbort, { once: true })
    img.onload = () => {
      signal?.removeEventListener('abort', onAbort)
      if (!img.naturalWidth || !img.naturalHeight) reject(new Error('The image has no pixels.'))
      else resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      signal?.removeEventListener('abort', onAbort)
      reject(new Error('The image could not be decoded.'))
    }
    img.src = src
  })
}

// ---------------------------------------------------------------------------
// Video
// ---------------------------------------------------------------------------

export interface LoadedVideo extends MediaSize {
  bytes: ArrayBuffer
  mimeType: string
}

const VIDEO_MIME_BY_EXTENSION: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  ogv: 'video/ogg',
  ogg: 'video/ogg',
}

export function guessVideoMimeType(url: string, declared: string): string {
  if (declared.startsWith('video/')) return declared.split(';')[0]
  return VIDEO_MIME_BY_EXTENSION[urlExtension(url)] ?? 'video/mp4'
}

export async function loadVideoSource(url: string, signal?: AbortSignal): Promise<LoadedVideo> {
  const blob = await fetchMediaBlob(url, { signal, maxBytes: MAX_VIDEO_BYTES })
  const mimeType = guessVideoMimeType(url, blob.type)
  const bytes = await blob.arrayBuffer()
  throwIfAborted(signal)
  // If this browser can't decode it, the sandbox can't either: say so here, once.
  const size = await videoSize(new Blob([bytes], { type: mimeType }), signal)
  return { bytes, mimeType, ...size }
}

function videoSize(blob: Blob, signal?: AbortSignal): Promise<MediaSize> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const video = document.createElement('video')
    const finish = (result: MediaSize | Error | DOMException) => {
      signal?.removeEventListener('abort', onAbort)
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(url)
      if (result instanceof Error || result instanceof DOMException) reject(result)
      else resolve(result)
    }
    const onAbort = () => finish(abortError())
    signal?.addEventListener('abort', onAbort, { once: true })
    video.muted = true
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      if (video.videoWidth && video.videoHeight) finish({ width: video.videoWidth, height: video.videoHeight })
      else finish(new Error('The video has no picture.'))
    }
    video.onerror = () => finish(new Error('This video format cannot be played in this browser.'))
    video.src = url
  })
}

export function videoMessage(video: LoadedVideo): ShaderInputMessage {
  const payload: ShaderVideoPayload = { bytes: video.bytes.slice(0), mimeType: video.mimeType }
  return { data: payload, transfer: [payload.bytes] }
}

// ---------------------------------------------------------------------------
// 3D model
// ---------------------------------------------------------------------------

export async function loadModelSource(url: string, signal?: AbortSignal): Promise<ShaderModelData> {
  const blob = await fetchMediaBlob(url, { signal, maxBytes: MAX_MODEL_BYTES })
  const bytes = await blob.arrayBuffer()
  throwIfAborted(signal)
  return parseGlb(bytes)
}

/** A copy of the model whose typed arrays can all be transferred. */
export function modelMessage(model: ShaderModelData): ShaderInputMessage {
  const transfer: Transferable[] = []
  const copy = <T extends Float32Array | Uint32Array>(array: T | null): T | null => {
    if (!array) return null
    const clone = array.slice() as T
    transfer.push(clone.buffer)
    return clone
  }
  const payload: ShaderModelData = {
    meshes: model.meshes.map((mesh) => ({
      positions: copy(mesh.positions) ?? new Float32Array(0),
      normals: copy(mesh.normals),
      uvs: copy(mesh.uvs),
      indices: copy(mesh.indices),
    })),
    bounds: { min: [...model.bounds.min], max: [...model.bounds.max] },
  }
  return { data: payload, transfer }
}

// ---------------------------------------------------------------------------
// All inputs
// ---------------------------------------------------------------------------

/** A connected input, loaded and ready to send. Audio is handled by `audio-source.ts`. */
export interface LoadedShaderInput {
  type: Exclude<ShaderInputType, 'audio'>
  /** Natural size, for image and video. */
  size?: MediaSize
  message(): ShaderInputMessage
}

export async function loadShaderInput(type: Exclude<ShaderInputType, 'audio'>, source: string, signal?: AbortSignal): Promise<LoadedShaderInput> {
  switch (type) {
    case 'image': {
      const image = await loadImageSource(source, signal)
      return { type, size: { width: image.width, height: image.height }, message: () => ({ data: image.dataUrl, transfer: [] }) }
    }
    case 'video': {
      const video = await loadVideoSource(source, signal)
      return { type, size: { width: video.width, height: video.height }, message: () => videoMessage(video) }
    }
    case '3d': {
      const model = await loadModelSource(source, signal)
      return { type, message: () => modelMessage(model) }
    }
    case 'text':
      return { type, message: () => ({ data: source, transfer: [] }) }
  }
}
